import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useAirports } from "@/hooks/flight/useAirports"
import { getOrderCheckoutPath, ProductType } from "@/constants/booking/order"
import Text from "@/components/common/Typography/Text"
import { getOrderDetail } from "@/utils/booking/getOrderDetail"
import { getQueryStringSearchFlightDynamic } from "@/utils/string/getQueryStringSearchFlightDynamic"
import { faMoneyBill, faSearch } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, Row, Tag } from "antd"
import dayjs from "dayjs"
import { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type TimmerCheckoutProps = {
  /** End of the payment countdown (the order's expiry). */
  deadline: string,
  /** The pay button is hidden once this little time (seconds) is left; the countdown keeps running. */
  minPayRemainingSeconds?: number,
  orderCode: any,
  productType?: string,
  orderData?: any,
  onDelete?: () => void
}

const TimmerCheckout: FC<TimmerCheckoutProps> = ({ deadline, minPayRemainingSeconds = 0, orderCode, productType, orderData, onDelete }) => {
  const [timmer, setTimmer] = useState<number>(-1)
  const router = useTERouter()
  const [loading, setLoading] = useState<boolean>(false)
  const interval = useRef<any>(null)
  const isDesktop = useIsDesktop()
  const airports = useAirports()
  const { getValidDayjs } = useTETranslation()
  const { t } = useTranslation()

  const checkoutPath = getOrderCheckoutPath(productType, orderCode)

  const handleClick = () => {
    if (!checkoutPath) return
    setLoading(true)
    router.push(checkoutPath)
    setLoading(false)
  }

  useEffect(() => {
    if (!orderCode) return;
    if (interval.current) {
      clearInterval(interval.current)
    }
    interval.current = setInterval(() => {
      setTimmer(() => {
        const nextValue = getValidDayjs(deadline).diff(dayjs(), 'second')
        if (nextValue <= 0) {
          clearInterval(interval.current)
          return 0
        }
        return nextValue
      })
    }, 1000)
  }, [deadline, orderCode])

  // mm:ss, or h:mm:ss for long holds (e.g. hotels).
  const pad = (value: number) => `${value}`.padStart(2, "0")
  const hours = Math.floor(timmer / 3600)
  const minutes = Math.floor((timmer % 3600) / 60)
  const seconds = timmer % 60
  const countdown = hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`
  const canPay = !!checkoutPath && timmer > minPayRemainingSeconds

  if (timmer === -1) {
    return <></>
  }

  return (
    <Row style={{ marginTop: 12 }} gutter={[10, 10]} align='middle' justify='space-between'>
      <Col {...(isDesktop ? {} : { span: 24 })}>
        <Tag style={{ whiteSpace: 'normal', ...(isDesktop ? {} : { width: '100%' }) }} color={timmer <= 0 ? 'red' : 'orange'}>
          {timmer <= 0
            ? t("order.payment_timeout")
            : t("order.payment_timmer", { timmer: countdown })
          }
        </Tag>
      </Col>
      <Col {...(isDesktop ? {} : { span: 24 })}>
        {timmer > 0 ? (canPay ? (
          <Button
            type='primary'
            onClick={(e) => {
              e.stopPropagation()
              handleClick()
            }}
            block={!isDesktop}
            loading={loading}
            icon={<FontAwesomeIcon icon={faMoneyBill} />}
          >
            {t("booking.pay_now")}
          </Button>
        ) : (
          // Last minutes before the expiry: too late to start a payment.
          <Text type="warning">{t("order.payment_closing")}</Text>
        )) : productType === ProductType.FLIGHT && (
          <Button
            type='primary'
            onClick={(e) => {
              e.stopPropagation()
              onDelete && onDelete()
              setLoading(true)
              getOrderDetail({ orderCode })
                .then((res) => {
                  const fareDatas = res.tickets.reduce((arr: any, ticket: any) => arr.concat(ticket.metaData.fareData), [])
                  const fareData = fareDatas[0]
                  const firstInventory = fareData?.flights[0]?.inventories[0]
                  const itinerary = fareDatas?.some((fare: any) => fare?.flights?.some((f: any) => f.leg > 0))

                  router.push(`/flights/result?${getQueryStringSearchFlightDynamic({
                    chd: firstInventory?.chd,
                    inf: firstInventory?.inf,
                    adt: firstInventory?.adt,
                    cabinClass: '',
                    itinerary: itinerary,
                    isMultiCities: !itinerary && fareData.length > 1,
                    flights: fareDatas.reduce((obj: any, fare: any, idx: any) => {
                      const segments = fare.flights[0]?.segments
                      const firstSeg = segments[0]
                      const lastSeg = segments[segments.length - 1]
                      obj[idx] = {
                        dateFrom: dayjs(firstSeg?.startTime).format("YYYY-MM-DD"),
                        dateTo: dayjs(lastSeg?.endTime).format("YYYY-MM-DD"),
                        flight: {
                          ...fare.flights[0],
                          inventory: fare.flights[0]?.inventories[0],
                        },
                        isReturn: fare.flights[0]?.leg > 0,
                        fromAirport: airports[firstSeg?.startPoint],
                        toAirport: airports[lastSeg?.endPoint],
                      }
                      return obj
                    }, {})
                  })}`)
                }).catch((error) => {
                  console.error('error', error)
                }).finally(() => {
                  setLoading(false)
                })
            }}
            loading={loading}
            icon={<FontAwesomeIcon icon={faSearch} />}
          >
            {t("flight.search_other_flights")}
          </Button>
        )}
      </Col>
    </Row>
  )
}

export default TimmerCheckout